import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { parseValidHttpsUrl } from 'common/parse-valid-https-url';
import {
  getSessionItem,
  removeSessionItem,
  setSessionItem,
} from 'utils/session-storage';

const BOOKING_IFRAME_SESSION_KEY = 'medibank-pp-booking-iframe-session';
const IN_PERSON_LANDING_PARAMS_KEY = 'medibank-pp-in-person-landing-params';

export type BookingIframeSessionPayload = {
  bookingLink: string;
  returnPath: string;
};

/**
 * Path (pathname + search) captured when the masonry action fires so the
 * booking iframe deeplink targets the screen the user was on.
 */
export function getBookAppointmentModalTargetPath(): string {
  if (typeof window === 'undefined') {
    return APP_MANAGED_ROUTES.HOME;
  }
  return `${window.location.pathname}${window.location.search}`;
}

export function stripBookingLinkFromLocationState(
  state: unknown,
): Record<string, unknown> | undefined {
  if (state === null || state === undefined || typeof state !== 'object') {
    return undefined;
  }
  const rest = Object.fromEntries(
    Object.entries(state as Record<string, unknown>).filter(
      ([key]) => key !== 'bookingLink',
    ),
  );
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/**
 * Persists an in-progress booking iframe session for the current screen so a
 * full page refresh can reopen the modal. Cleared when the user finishes or
 * leaves booking ({@link clearBookingIframeSession}).
 */
export function persistBookingIframeSession(
  bookingLink: string,
  returnPath: string,
): void {
  const link = parseValidHttpsUrl(bookingLink);
  if (!link) {
    return;
  }
  const payload: BookingIframeSessionPayload = {
    bookingLink: link,
    returnPath,
  };
  setSessionItem(BOOKING_IFRAME_SESSION_KEY, JSON.stringify(payload));
}

/**
 * Parses the raw stored booking session and validates it against
 * `currentReturnPath`. Returns the booking URL only when the payload is a valid
 * https link whose `returnPath` matches the current screen.
 */
function parseValidBookingIframeSession(
  raw: string,
  currentReturnPath: string,
): string | undefined {
  try {
    const parsed = JSON.parse(raw) as Partial<BookingIframeSessionPayload>;
    const link = parseValidHttpsUrl(parsed.bookingLink);
    if (
      !link ||
      typeof parsed.returnPath !== 'string' ||
      parsed.returnPath !== currentReturnPath
    ) {
      return undefined;
    }
    return link;
  } catch {
    return undefined;
  }
}

/**
 * Pure read — returns the stored booking URL only when it matches
 * `currentReturnPath`. Does NOT mutate sessionStorage; call
 * {@link clearStaleBookingIframeSession} in a `useEffect` to evict stale
 * entries without causing render-time side effects.
 */
export function readStoredBookingIframeSession(
  currentReturnPath: string,
): string | undefined {
  const raw = getSessionItem(BOOKING_IFRAME_SESSION_KEY);
  if (!raw) {
    return undefined;
  }
  return parseValidBookingIframeSession(raw, currentReturnPath);
}

/**
 * Removes a stored booking session that is stale (invalid payload or
 * `returnPath` does not match `currentReturnPath`). Intended to be called
 * from a `useEffect` so sessionStorage is never mutated during render.
 */
export function clearStaleBookingIframeSession(
  currentReturnPath: string,
): void {
  const raw = getSessionItem(BOOKING_IFRAME_SESSION_KEY);
  if (!raw) {
    return;
  }
  if (!parseValidBookingIframeSession(raw, currentReturnPath)) {
    removeSessionItem(BOOKING_IFRAME_SESSION_KEY);
  }
}

export function clearBookingIframeSession(): void {
  removeSessionItem(BOOKING_IFRAME_SESSION_KEY);
}

/**
 * Persists the query string (e.g. `?systemId=...`) of the in-person
 * consultation landing page. The in-person booking flow routes the user into
 * the care-finder module (a backend masonry navigation), which drops these
 * params, so they are stashed here on landing and restored when the user exits
 * the booking flow back to the landing page.
 */
export function persistInPersonLandingParams(search: string): void {
  setSessionItem(IN_PERSON_LANDING_PARAMS_KEY, search);
}

/**
 * Reads the stored in-person landing page query string. Returns an empty string
 * when nothing is stored so it can be safely appended to a path.
 */
export function readInPersonLandingParams(): string {
  return getSessionItem(IN_PERSON_LANDING_PARAMS_KEY) ?? '';
}
